import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";

/**
 * Sticky progress card shown during a deep-research run.
 *
 * Contract:
 *   - Visible from the moment research kicks off until `visible=false`
 *     (parent sets this when full synthesis lands as hasData).
 *   - Renders a rotating list of pipeline-stage messages so the rep sees the
 *     research is alive throughout the ~70s run, not just the first 6s.
 *   - Step pacing tuned so the line list lasts roughly the duration of a
 *     full research; the last line keeps spinning until `visible=false`.
 *   - Has its own elapsed-time counter (since the left-panel timer was
 *     removed in favor of a wider right column).
 */

const DEFAULT_LINES = (contactName, sellerName) => {
  const who = contactName || "this prospect";
  return [
    `🔎 Searching the web for ${who}…`,
    `📇 Mapping social handles & digital footprint`,
    `🎯 Fanning out 15+ specialized SERP queries`,
    `🧠 Generating queries tailored to ${sellerName || "your pitch"}`,
    `📚 Pulling sources across thought leadership, news, podcasts, hiring & funding`,
    `⚡ Triaging the top sources to deep-dive`,
    // Post-SERP — research is still running through synthesis stages.
    `🔍 Diving deep to extract key facts from the top sources`,
    `🧩 Connecting career history with ${sellerName || "seller"}'s ICP`,
    `🛡  Identifying objections this prospect is likely to raise`,
    `✨ Crafting selling angles and opener hooks for your reps`,
    `📝 Finalizing the personalized brief…`
  ];
};

const ResearchingPlaceholder = observer(({
  contactName,
  sellerName,
  visible,
  // Optional override — when set, replaces the default contact-DR lines.
  // Used by AccountIntelligenceSection to show account-specific pipeline steps.
  customLines = null,
  subjectName = null,
  // Pacing — a full Contact DR takes ~70s and we have ~11 lines, so
  // ~6500ms/line lands the last line just as research completes. The very
  // last line keeps spinning until parent sets visible=false, so a slightly
  // shorter pace is safe — the spinner-on-last-line covers the gap.
  lineIntervalMs = 6500
}) => {
  const [shown, setShown] = useState(1);
  const [elapsedMs, setElapsedMs] = useState(0);
  const lines = customLines && customLines.length > 0
      ? customLines
      : DEFAULT_LINES(contactName, sellerName);

  // Reveal lines one by one. Stop revealing past the list — last line keeps
  // showing as the active step (with spinner) until visible flips false.
  useEffect(() => {
    if (!visible) return undefined;
    if (shown >= lines.length) return undefined;
    const t = setTimeout(() => setShown((s) => Math.min(s + 1, lines.length)), lineIntervalMs);
    return () => clearTimeout(t);
  }, [visible, shown, lines.length, lineIntervalMs]);

  // Elapsed-time counter. Updates every 100ms for sub-second precision in
  // the display ("4.3s"). Resets when card becomes invisible.
  useEffect(() => {
    if (!visible) {
      setElapsedMs(0);
      setShown(1);
      return undefined;
    }
    const startedAt = Date.now();
    const interval = setInterval(() => setElapsedMs(Date.now() - startedAt), 100);
    return () => clearInterval(interval);
  }, [visible]);

  if (!visible) return null;

  const elapsedSec = (elapsedMs / 1000).toFixed(1);

  return (
    <div className="drResearchingCard">
      <div className="drResearchingHeader">
        <span className="drResearchingPulse" aria-hidden="true" />
        <span className="drResearchingTitle">
          Researching <strong>{subjectName || contactName || "prospect"}</strong>…
        </span>
        <span className="drResearchingTimer" title="Elapsed time">
          {elapsedSec}s
        </span>
      </div>
      <ul className="drResearchingLines">
        {lines.slice(0, shown).map((l, i) => {
          // The last revealed line is the "currently in progress" step (spinner).
          // All earlier lines are completed (tick). When `shown === lines.length`,
          // the final line stays as the active step until `visible=false`.
          const isCurrent = i === shown - 1;
          return (
            <li
              key={i}
              className={`drResearchingLine ${isCurrent ? "drResearchingLine--current" : ""}`}
            >
              {!isCurrent && <span className="drResearchingTick" aria-hidden="true" />}
              {isCurrent && <span className="drResearchingSpinner" aria-hidden="true" />}
              <span>{l}</span>
            </li>
          );
        })}
      </ul>
      <p className="drResearchingHint">
        Sources will stream in as they’re found. No need to wait for the full
        synthesis to start exploring.
      </p>
    </div>
  );
});

export default ResearchingPlaceholder;
